import { Component, OnInit, ViewChild, ElementRef, ChangeDetectorRef, Input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DocumentCreateOrUpdateEvent, CreateOrUpdateRecordDocumentRequest, DocumentPayload } from 'store/models/document.models';
import { IndividualRecord, CorporateRecord } from 'modules/shared/models/record.models';

import { SnackbarService } from 'app/services/snackbar.service';
import { DocumentsService } from 'modules/core/services/documents.service';

import { CustomerDocumentListComponent } from '../customer-document-list/customer-document-list.component';

@Component({
  selector: 'artemis-customer-document',
  templateUrl: './customer-document.component.html',
  styleUrls: ['./customer-document.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerDocumentComponent implements OnInit, OnDestroy {

  @ViewChild(CustomerDocumentListComponent, {static: false}) customerDocumentListComponent: CustomerDocumentListComponent;

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private _record: IndividualRecord | CorporateRecord;
  currentEditCustomerDocument: CreateOrUpdateRecordDocumentRequest;
  documentTypes: Array<string>;
  authenticites: Array<string>;
  loading: boolean = false;
  isShowDocumentUpdate: boolean;

  @Input()
  set record(record: IndividualRecord | CorporateRecord) {
    if (!record) {
      return;
    }
    this._record = record;
    this.loadDocumentsTypes(record);
    this.loadAuthenticities();
  }
  get record(): IndividualRecord | CorporateRecord {
    return this._record;
  }

  @ViewChild('uploadForm', {static: false}) uploadForm: ElementRef;
  @ViewChild('documentList', {static: true}) documentList: ElementRef;

  constructor(private documentsService: DocumentsService,
              private cdRef: ChangeDetectorRef,
              private snackBar: SnackbarService) { }

  ngOnInit() {
  }

   /**
   * Loads list of documents types
   * @method loadDocumentsTypes
   */
  private loadDocumentsTypes(record: IndividualRecord | CorporateRecord): void {
    this.documentsService
      .getDocumentTypes(record.recordType)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res: Array<string>) => {
          this.documentTypes = res;
          this.cdRef.detectChanges();
        },
        (error: any) => {
          this.snackBar.error('Error: Failed to load list of document types');
          this.documentTypes = [];
          this.cdRef.detectChanges();
        }
      );
  }

  /**
   * Loads list of authenticites for documents
   * @method loadAuthenticities
   */
  private loadAuthenticities(): void {
    this.documentsService
      .getAuthenticites()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res: Array<string>) => {
          this.authenticites = res;
          this.cdRef.detectChanges();
        },
        (error: any) => {
          this.snackBar.error('Error: Failed to load list of authenticities');
          this.authenticites = [];
          this.cdRef.detectChanges();
        }
      );
  }

  /**
   * Handler for edit document event from customer documents list
   * @method editDocumentHandler
   */
  editDocumentHandler(customerDocument: any): void {
    if (customerDocument) {
      this.currentEditCustomerDocument = customerDocument;
      this.isShowDocumentUpdate = true;
    } else if (customerDocument === null) {
      this.currentEditCustomerDocument = {
        authenticity: '',
        file: null,
        docName: '',
        backDocName: '',
        docType: null,
        expiryDate: '',
        issueDate: '',
        recordId: this.record.id,
        showExpiryAlerts: true
      };
      this.isShowDocumentUpdate = true;
    } else {
      this.currentEditCustomerDocument = null;
      this.isShowDocumentUpdate = false;
    }
    this.cdRef.detectChanges();
    if (this.isShowDocumentUpdate) {
      this.uploadForm.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  /**
   * Handler for the save button in update documents card
   * @method documentUpdated
   */
  documentUpdated(data: DocumentCreateOrUpdateEvent): void {
    if (data.error) {
      this.snackBar.error(data.error);
    } else {
      this.customerDocumentListComponent.customerDocumentsDataSource.loadDocuments(<DocumentPayload>{
        sort: '-updated_at',
        offset: 0
      });

      if (data.mode === 'CREATE') {
        this.snackBar.success('Document successfully uploaded');
      } else {
        this.snackBar.success('Document successfully updated');
      }

      this.currentEditCustomerDocument = null;
      this.isShowDocumentUpdate = false;
    }
  }

  // Set loading
  setLoading(loading: boolean): void {
    this.loading = loading;
    this.cdRef.detectChanges();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
